/**
 * Hook for sidebar drag-to-resize functionality.
 *
 * Enables dragging the sidebar edge to resize width.
 * Dragging below threshold collapses the sidebar.
 * Dragging from collapsed state expands to cursor position once past minimum width.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { playCollapsePop, playExpandPop } from "@animus/sounds";

import {
  SIDEBAR_COLLAPSE_THRESHOLD,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
  useSidebarStore,
} from "./store";

interface UseSidebarResizeResult {
  isResizing: boolean;
  expandProgress: number;
  elasticWidth: number | null;
  cursorX: number | null;
  cursorY: number | null;
  handleMouseDown: (e: React.MouseEvent) => void;
}

const RUBBER_BAND_COEFFICIENT = 0.55;
const RUBBER_BAND_MAX_STRETCH = 60;
const MIN_CONTENT_WIDTH = 400;

function applyRubberBand(overshoot: number): number {
  const x = Math.abs(overshoot);
  const c = RUBBER_BAND_COEFFICIENT;
  const d = RUBBER_BAND_MAX_STRETCH;
  const rubberBanded = (1.0 - 1.0 / ((x * c) / d + 1.0)) * d;
  return rubberBanded * Math.sign(overshoot);
}

function computeExpandProgress(targetWidth: number): number {
  return Math.max(
    0,
    Math.min(
      1,
      (targetWidth - SIDEBAR_COLLAPSED_WIDTH) /
        (SIDEBAR_MIN_WIDTH - SIDEBAR_COLLAPSED_WIDTH)
    )
  );
}

function computeElasticOvershoot(
  targetWidth: number,
  boundaryWidth: number,
  direction: "over" | "under"
): number {
  const overshoot =
    direction === "over"
      ? targetWidth - boundaryWidth
      : boundaryWidth - targetWidth;
  const elasticAmount = applyRubberBand(overshoot);
  return direction === "over"
    ? boundaryWidth + elasticAmount
    : boundaryWidth - elasticAmount;
}

function windowCanExpand(): boolean {
  return window.innerWidth >= SIDEBAR_MIN_WIDTH + MIN_CONTENT_WIDTH;
}

export function useSidebarResize(): UseSidebarResizeResult {
  const [isResizing, setIsResizing] = useState(false);
  const [expandProgress, setExpandProgress] = useState(0);
  const [elasticWidth, setElasticWidth] = useState<number | null>(null);
  const [cursorX, setCursorX] = useState<number | null>(null);
  const [cursorY, setCursorY] = useState<number | null>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const layoutOffsetRef = useRef(0);
  const expandSoundPlayedRef = useRef(false);
  const collapseSoundPlayedRef = useRef(false);

  const { width, isCollapsed, setWidth, setPreferredWidth, setCollapsed } =
    useSidebarStore();

  const handleCollapsedDrag = useCallback(
    (targetWidth: number, clientX: number) => {
      setExpandProgress(computeExpandProgress(targetWidth));

      if (targetWidth > SIDEBAR_COLLAPSED_WIDTH) {
        setElasticWidth(
          computeElasticOvershoot(targetWidth, SIDEBAR_COLLAPSED_WIDTH, "over")
        );
      } else {
        setElasticWidth(null);
      }

      if (targetWidth >= SIDEBAR_MIN_WIDTH) {
        if (windowCanExpand()) {
          const clampedWidth = Math.min(SIDEBAR_MAX_WIDTH, targetWidth);
          setCollapsed(false);
          setWidth(clampedWidth);
          setElasticWidth(null);
          startXRef.current = clientX;
          startWidthRef.current = clampedWidth;
          if (!expandSoundPlayedRef.current) {
            playExpandPop();
            expandSoundPlayedRef.current = true;
          }
        }
        collapseSoundPlayedRef.current = false;
      }
    },
    [setCollapsed, setWidth]
  );

  const handleExpandedDrag = useCallback(
    (targetWidth: number) => {
      if (targetWidth < SIDEBAR_MIN_WIDTH) {
        setElasticWidth(
          computeElasticOvershoot(targetWidth, SIDEBAR_MIN_WIDTH, "under")
        );
        if (targetWidth < SIDEBAR_COLLAPSE_THRESHOLD) {
          setCollapsed(true);
          setElasticWidth(null);
          if (!collapseSoundPlayedRef.current) {
            playCollapsePop();
            collapseSoundPlayedRef.current = true;
          }
          expandSoundPlayedRef.current = false;
        }
        return;
      }

      if (targetWidth > SIDEBAR_MAX_WIDTH) {
        setElasticWidth(
          computeElasticOvershoot(targetWidth, SIDEBAR_MAX_WIDTH, "over")
        );
        return;
      }

      setElasticWidth(null);
      setWidth(targetWidth);
    },
    [setCollapsed, setWidth]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      setCursorX(e.clientX);
      setCursorY(e.clientY);
      const targetWidth = e.clientX - layoutOffsetRef.current;
      const currentlyCollapsed = useSidebarStore.getState().isCollapsed;

      if (currentlyCollapsed) {
        handleCollapsedDrag(targetWidth, e.clientX);
      } else {
        handleExpandedDrag(targetWidth);
      }
    },
    [handleCollapsedDrag, handleExpandedDrag]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    setExpandProgress(0);
    setElasticWidth(null);
    setCursorX(null);
    setCursorY(null);
    expandSoundPlayedRef.current = false;
    collapseSoundPlayedRef.current = false;

    const currentWidth = useSidebarStore.getState().width;
    const currentCollapsed = useSidebarStore.getState().isCollapsed;

    if (!currentCollapsed && currentWidth >= SIDEBAR_MIN_WIDTH) {
      setPreferredWidth(currentWidth);
    }
  }, [setPreferredWidth]);

  useEffect(() => {
    if (!isResizing) {
      return;
    }

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : width;
      layoutOffsetRef.current =
        e.clientX - (isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : width);
    },
    [width, isCollapsed]
  );

  return {
    isResizing,
    expandProgress,
    elasticWidth,
    cursorX,
    cursorY,
    handleMouseDown,
  };
}
